import { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import Papa from 'papaparse';
import { csvApi, CSVData } from '../services/api';

interface EditCSVModalProps {
  open: boolean;
  folder: string;
  filename: string;
  onClose: () => void;
  onSave: () => void;
}

export const EditCSVModal: FC<EditCSVModalProps> = ({
  open,
  folder,
  filename,
  onClose,
  onSave,
}) => {
  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [csvText, setCsvText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCSV = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await csvApi.parseCSV(folder, filename);
      setCsvData(data);
      // Convert back to CSV text for editing
      const csv = [
        data.headers.join(','),
        ...data.rows.map((row) =>
          data.headers
            .map((h) => {
              const val = row[h] || '';
              return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
            })
            .join(','),
        ),
      ].join('\n');
      setCsvText(csv);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!csvData) return;

    setLoading(true);
    setError(null);
    try {
      // Parse the edited CSV text
      Papa.parse(csvText, {
        header: true,
        complete: async (results) => {
          try {
            const updatedData: CSVData = {
              headers: results.meta.fields || [],
              rows: results.data as Record<string, string>[],
            };
            await csvApi.updateCSV(folder, filename, updatedData);
            onSave();
            onClose();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save CSV');
          } finally {
            setLoading(false);
          }
        },
        error: (error) => {
          setError(`CSV parse error: ${error.message}`);
          setLoading(false);
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save CSV');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit {filename}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          fullWidth
          multiline
          rows={15}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder="CSV content"
          sx={{ fontFamily: 'monospace', mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
